import type { TourDefinition } from '../../../models/tour.interfaces';

const hasFilledInputs = (el: HTMLElement, minInputs = 1): boolean => {
  const inputs = el.querySelectorAll('input:not([disabled])');
  let filled = 0;
  inputs.forEach((input: Element) => {
    if ((input as HTMLInputElement).value.trim() !== '') filled++;
  });
  return filled >= minInputs;
};

const hasSelectedCards = (el: HTMLElement): boolean => {
  // Cards selected appear as <article> tags inside the SearchWithCardsBuilder
  const cards = el.querySelectorAll('article');
  return cards.length > 0;
};

const hasCheckedRadios = (el: HTMLElement): boolean => {
  const radios = el.querySelectorAll('input[type="radio"]');
  let checked = false;
  radios.forEach((radio: Element) => {
    if ((radio as HTMLInputElement).checked) checked = true;
  });
  return checked;
};

export const servicesTour: TourDefinition = {
  module: 'services',
  steps: [
    {
      id: 'servicios_step_1',
      order: 1,
      target: '[data-testid="btn-add-service"]',
      title: 'Crea un nuevo servicio',
      body: 'Haz clic aquí para comenzar el registro de un nuevo servicio.',
      requiredAction: 'click',
      nextStepTrigger: 'onElementClick',
      placement: 'bottom',
      skippable: true,
    },
    {
      id: 'servicios_step_2',
      order: 2,
      target: '[data-testid="service-basic-info"]',
      title: 'Datos básicos del servicio',
      body: 'Ingresa el nombre del servicio y selecciona o crea una categoría.',
      completedBody: '¡Perfecto! Los datos básicos están completos. Haz clic en Siguiente para continuar.',
      requiredAction: 'fill',
      nextStepTrigger: 'onNextButton',
      placement: 'right',
      skippable: false,
      completionCheck: (el) => {
        const nameInput = el.querySelector('input');
        const hasName = nameInput ? nameInput.value.trim() !== '' : false;
        // Category cards appear as <article> inside [data-testid="service-category"]
        const categoryContainer = el.querySelector('[data-testid="service-category"]');
        const hasCategory = categoryContainer ? categoryContainer.querySelectorAll('article').length > 0 : false;
        return hasName && hasCategory;
      },
    },
    {
      id: 'servicios_step_3',
      order: 3,
      target: '[data-testid="service-prices-only"]',
      title: 'Configura precios y duración',
      body: 'Ponle un nombre al precio (ej: "Cabello corto", "Cabello largo"), define su duración y valor. Así podrás diferenciar tarifas para un mismo servicio.',
      completedBody: '¡Muy bien! El precio está configurado. Haz clic en Siguiente para continuar.',
      requiredAction: 'fill',
      nextStepTrigger: 'onNextButton',
      placement: 'top',
      skippable: false,
      hideSelectors: ['[data-testid="btn-add-price"] button[class*="ButtonAddMore"]'],
      completionCheck: (el) => {
        const inputs = el.querySelectorAll('input:not([disabled])');
        const priceName = inputs[0] ? (inputs[0] as HTMLInputElement).value.trim() : '';
        const priceValue = inputs[1] ? parseFloat((inputs[1] as HTMLInputElement).value) : 0;
        // Check IGV disabled input has value (calculated from price)
        const igvInput = el.querySelector('input[disabled]') as HTMLInputElement;
        const hasPrice = igvInput ? parseFloat(igvInput.value) > 0 : false;
        // Check duration: find the text that matches "HS" label area, then extract time
        // Duration text appears as: HS 00 : 00 (or HS 01 : 30, etc.)
        // We use textContent of the duration label container
        const durationLabel = Array.from(el.querySelectorAll('*')).find(
          (node) => node.children.length === 0 && node.textContent === 'HS'
        );
        if (!durationLabel || !durationLabel.parentElement) return false;
        const durationContainer = durationLabel.parentElement;
        const timeText = durationContainer.textContent || '';
        // Extract hours and minutes: "HS00:00" or "HS 00 : 30"
        const match = timeText.replace(/\s/g, '').match(/HS(\d{2}):(\d{2})/);
        const hasDuration = match ? (parseInt(match[1]) > 0 || parseInt(match[2]) > 0) : false;
        return !!priceName && hasPrice && hasDuration;
      },
    },
    {
      id: 'servicios_step_4',
      order: 4,
      target: '[data-testid="service-employee-assign"]',
      title: 'Selecciona quién realiza este servicio',
      body: 'Escribe el nombre del empleado en el buscador y selecciónalo de la lista. Este paso es obligatorio.',
      completedBody: '¡Listo! Empleado asignado. Haz clic en Siguiente para continuar.',
      requiredAction: 'select',
      nextStepTrigger: 'onNextButton',
      placement: 'right',
      skippable: false,
      spotlightExtraHeight: 150,
      completionCheck: (el) => hasSelectedCards(el),
    },
    {
      id: 'servicios_step_5',
      order: 5,
      target: '[data-testid="service-lounges"]',
      title: 'Selecciona los locales',
      body: 'Marca en qué locales estará disponible este servicio.',
      completedBody: '¡Genial! Local seleccionado. Haz clic en Siguiente para continuar.',
      requiredAction: 'select',
      nextStepTrigger: 'onNextButton',
      placement: 'right',
      skippable: false,
      completionCheck: (el) => hasCheckedRadios(el),
    },
    {
      id: 'servicios_step_6',
      order: 6,
      target: '[data-testid="btn-save-service"]',
      title: 'Guarda el servicio',
      body: 'Una vez ingresados todos los datos obligatorios, haz clic en Guardar.',
      requiredAction: 'click',
      nextStepTrigger: 'onElementClick',
      placement: 'bottom',
      skippable: false,
    },
    {
      id: 'servicios_step_7',
      order: 7,
      target: '[data-testid="services-list"]',
      title: 'Servicio registrado',
      body: 'Aquí verás tu nuevo servicio listo para editar o eliminar cuando lo necesites.',
      requiredAction: 'none',
      nextStepTrigger: 'onFinish',
      placement: 'top',
      skippable: true,
    },
  ],
};
